import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import 'prop-types';
import { isDate, intervalToDuration, isFuture, format } from 'date-fns';
import * as React from 'react';
import styled from 'styled-components';
import { useTranslations } from '../../hooks/use_translations.js';
import { useThemeContext } from '../../styles/themes/themes.js';
import { dateFnsLocaleMap, getDateFormat } from '../../utils/date.js';
import { Tooltip } from '../Tooltip/Tooltip.js';
import { jsx } from 'react/jsx-runtime';

var _excluded = ["date", "formatDate", "languageCode", "locale", "tooltipProps", "variant"];
var DateContainer = styled.div.withConfig({
  displayName: "Timestamp__DateContainer",
  componentId: "sc-pptg90-0"
})(["&&{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:42px;}"]);
var Timestamp = /*#__PURE__*/React.memo( /*#__PURE__*/React.forwardRef(function Timestamp2(props, ref) {
  var _useThemeContext = useThemeContext(),
      localization = _useThemeContext.localization;

  var _useTranslations = useTranslations(localization === null || localization === void 0 ? void 0 : localization.translations),
      t = _useTranslations.t;

  var date = props.date,
      formatDate = props.formatDate,
      languageCode = props.languageCode,
      locale = props.locale,
      tooltipProps = props.tooltipProps,
      _props$variant = props.variant,
      variant = _props$variant === void 0 ? "relative" : _props$variant,
      rest = _objectWithoutProperties(props, _excluded);

  var themeProvidedLocale = (localization === null || localization === void 0 ? void 0 : localization.languageCode) && dateFnsLocaleMap[localization === null || localization === void 0 ? void 0 : localization.languageCode] || dateFnsLocaleMap["en-US"];
  var componentProvidedLocale = languageCode && dateFnsLocaleMap[languageCode] || locale;
  var currentLocale = componentProvidedLocale || themeProvidedLocale;

  var startDate = function () {
    if (isDate(date)) {
      return date;
    }

    if (isNaN(Date.parse(date))) {
      if (process.env.NODE_ENV !== "production") {
        console.error("ERROR: Unable to create Date from given date: '".concat(date, "'"));
      }

      return void 0;
    }

    return new Date(date);
  }();

  var timestamp = startDate && intervalToDuration({
    start: startDate,
    end: new Date()
  });

  var getDateAndTime = function getDateAndTime(dateToSplit) {
    var dateModifier = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "default";
    var modifierLookup = {
      short: "P",
      long: "PPP",
      default: getDateFormat(currentLocale)
    };
    var splitDate = format(dateToSplit, modifierLookup[dateModifier], {
      locale: currentLocale
    });
    var splitTime = format(dateToSplit, "p", {
      locale: currentLocale
    });
    return [splitDate, splitTime];
  };

  var getTooltipContent = function getTooltipContent() {
    if (!timestamp || !startDate) return void 0;

    var _getDateAndTime = getDateAndTime(startDate),
        _getDateAndTime2 = _slicedToArray(_getDateAndTime, 2),
        splitDate = _getDateAndTime2[0],
        splitTime = _getDateAndTime2[1];

    return t("Timestamp.defaultDateTime", {
      dateString: splitDate,
      timeString: splitTime
    });
  };

  var getFormattedDate = function getFormattedDate() {
    if (!startDate || !timestamp) return t("Timestamp.invalidDate");
    var dateFormatted;

    var _getDateAndTime3 = getDateAndTime(startDate),
        _getDateAndTime4 = _slicedToArray(_getDateAndTime3, 2),
        splitDate = _getDateAndTime4[0],
        splitTime = _getDateAndTime4[1];

    switch (true) {
      case variant === "dateOnly":
        dateFormatted = "".concat(format(startDate, "PP", {
          locale: currentLocale
        }));
        break;

      case variant === "dateOnlyLong":
        dateFormatted = "".concat(format(startDate, "PPP", {
          locale: currentLocale
        }));
        break;

      case variant === "dateOnlyShort":
        dateFormatted = "".concat(format(startDate, "P", {
          locale: currentLocale
        }));
        break;

      case timestamp.years > 0 || variant === "absolute" || isFuture(startDate):
        dateFormatted = t("Timestamp.defaultDateTime", {
          dateString: splitDate,
          timeString: splitTime
        });
        break;

      case timestamp.months > 0 || timestamp.days > 1:
        dateFormatted = t("Timestamp.defaultDateTime", {
          dateString: splitDate,
          timeString: splitTime
        });
        break;

      case timestamp.days === 1:
        dateFormatted = t("Timestamp.yesterdayAt", {
          timeString: splitTime
        });
        break;

      case timestamp.hours > 0:
        dateFormatted = timestamp.hours === 1 ? t("Timestamp.lessThanTwoHoursAgo") : t("Timestamp.lessThanOneDayAgo", {
          hoursString: timestamp.hours.toString()
        });
        break;

      case timestamp.minutes > 0:
        dateFormatted = timestamp.minutes === 1 ? t("Timestamp.lessThanTwoMinutesAgo") : t("Timestamp.lessThanOneHourAgo", {
          minutesString: timestamp.minutes.toString()
        });
        break;

      case timestamp.seconds >= 0:
        dateFormatted = t("Timestamp.lessThanOneMinuteAgo");
        break;

      default:
        if (process.env.NODE_ENV !== "production") {
          console.error("ERROR: Unable to format the date given: '".concat(startDate, "'"));
        }

        dateFormatted = t("Timestamp.invalidDate");
    }

    return dateFormatted;
  };

  var timestampOutputValue = getFormattedDate();
  return /*#__PURE__*/jsx(DateContainer, _objectSpread(_objectSpread({
    ref: ref
  }, rest), {}, {
    children: /*#__PURE__*/jsx(Tooltip, _objectSpread(_objectSpread({}, tooltipProps), {}, {
      content: getTooltipContent(),
      children: /*#__PURE__*/jsx("span", {
        children: formatDate ? formatDate(timestampOutputValue) : timestampOutputValue
      })
    }))
  }));
}));
Timestamp.displayName = "Timestamp";

export { Timestamp };
